import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { Col, Divider, List, Row, Tooltip } from 'antd';
import React from 'react';
import { CompanySetting } from '../../../constants/CompanySetting';
import { Company } from '../../../models/company-interface';
import { CompanySettings } from '../../../models/company-settings-interface';
import { MeetingSettings } from '../../../models/meeting-settings-interface';
import { Feature } from '../../../models/product-interface';

interface FeaturesListProps {
  companyDetails: Company | undefined;
  companyDocumentSettings: CompanySettings[] | undefined;
  companyMeetingSettings: MeetingSettings | undefined;
  features: Feature[];
}

interface FeatureItem {
  name: string;
  key: string;
  enabled: boolean;
}

export default function FeaturesList({
  companyDetails,
  companyDocumentSettings,
  companyMeetingSettings,
  features
}: FeaturesListProps) {
  if (!companyDetails)
    return null;

  return (
    <>
      <SingleSignOnFeature companyDetails={companyDetails} />
      <Divider style={{ marginTop: 16, marginBottom: 16 }} />
      <TwoFactorFeature companyDetails={companyDetails} />
      <Divider style={{ marginTop: 16, marginBottom: 16 }} />
      {(!!companyDocumentSettings && !!companyMeetingSettings) && 
        <>
          <NativeStorageFeature companyDetails={companyDetails} />
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
          <GabiiFeature companyDetails={companyDetails} />
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
        </>
      }
      <ActivityFeature companyDetails={companyDetails} hasActivity={features.some(x => x.id == 10)} />
      <Divider style={{ marginTop: 16, marginBottom: 0 }} />

      {(!!companyDocumentSettings && !!companyMeetingSettings) && 
        <FeatureItemList
          companyDetails={companyDetails}
          companyDocumentSettings={companyDocumentSettings}
          companyMeetingSettings={companyMeetingSettings}
        />
      }
    </>
  );
}

const SingleSignOnFeature = ({
  companyDetails,
}: {
  companyDetails: Company;
}) => (
  <Row align='middle'>
    <Col span={6}>Single Sign-On</Col>
    <Col span={18} style={{ textAlign: 'right' }}>
      {!companyDetails.singleSignOnAvailable ? (
        <CloseCircleFilled style={{ color: '#6c757d', fontSize: 20 }} />
      ) : !companyDetails.singleSignOnConfigured ? (
        'Not Configured'
      ) : !companyDetails.singleSignOnEnabled ? (
        'Disabled'
      ) : (
        <CheckCircleFilled style={{ color: '#28a745', fontSize: 20 }} />
      )}
    </Col>
  </Row>
);

const GabiiFeature = ({
  companyDetails,
}: {
  companyDetails: Company;
}) => (
  <Row align='middle'>
    <Col span={6}>Gabii AI</Col>
    <Col span={18} style={{ textAlign: 'right' }}>
      {!companyDetails.gabiiAvailable ? (
        <CloseCircleFilled style={{ color: '#6c757d', fontSize: 20 }} />
      ) : !companyDetails.gabiiEnabled ? (
        'Disabled'
      ) : (
        <CheckCircleFilled style={{ color: '#28a745', fontSize: 20 }} />
      )}
    </Col>
  </Row>
);

const NativeStorageFeature = ({
  companyDetails,
}: {
  companyDetails: Company;
}) => (
  <Row align='middle'>
    <Col span={6}>Native File Storage</Col>
    <Col span={18} style={{ textAlign: 'right' }}>
      {!companyDetails.nativeStorageAvailable ? (
        <CloseCircleFilled style={{ color: '#6c757d', fontSize: 20 }} />
      ) : !companyDetails.nativeStorageEnabled ? (
        'Disabled'
      ) : (
        <CheckCircleFilled style={{ color: '#28a745', fontSize: 20 }} />
      )}
    </Col>
  </Row>
);

const ActivityFeature = ({
  companyDetails,
  hasActivity
}: {
  companyDetails: Company;
  hasActivity: boolean;
}) => (
  <Row align='middle'>
    <Col span={6}>Activity Tracking</Col>
    <Col span={18} style={{ textAlign: 'right' }}>
      {!hasActivity ? (
        <CloseCircleFilled style={{ color: '#6c757d', fontSize: 20 }} />
      ) : !companyDetails.activityEnabled ? (
        'Disabled'
      ) : (
        <CheckCircleFilled style={{ color: '#28a745', fontSize: 20 }} />
      )}
    </Col>
  </Row>
);

const TwoFactorFeature = ({ companyDetails }: { companyDetails: Company }) => (
  <Row align='middle'>
    <Col span={6}>Two Factor</Col>
    <Col span={18} style={{ textAlign: 'right' }}>
      {!companyDetails.twoFactorAvailable ? (
        <CloseCircleFilled style={{ color: '#6c757d', fontSize: 20 }} />
      ) : !companyDetails.twoFactorEnabled ? (
        'Disabled'
      ) : companyDetails.singleSignOnEnabled ? (
        <Tooltip overlay='Two-Factor only applies to users using Govenda Sign-In'>
          <CheckCircleFilled style={{ color: '#e6cc00', fontSize: 20 }} />
        </Tooltip>
      ) : (
        <CheckCircleFilled style={{ color: '#28a745', fontSize: 20 }} />
      )}
    </Col>
  </Row>
);

const FeatureItemList = ({
  companyDetails,
  companyDocumentSettings,
  companyMeetingSettings
}: {
  companyDetails: Company;
  companyDocumentSettings: CompanySettings[]
  companyMeetingSettings: MeetingSettings;
}) => {
  const companyDocumentSettingsIds = companyDocumentSettings.map(
    (s) => s.settingId
  );

  const features: FeatureItem[] = [
    {
      name: 'Printing',
      key: 'printing',
      enabled: !!companyDocumentSettingsIds.includes(
        CompanySetting.PrintDocument
      ),
    },
    {
      name: 'Downloading',
      key: 'downloading',
      enabled: !!companyDocumentSettingsIds.includes(
        CompanySetting.DownloadDocument
      ),
    },
    {
      name: 'Annotating',
      key: 'annotating',
      enabled: !!companyDocumentSettingsIds.includes(
        CompanySetting.AnnotateDocument
      ),
    },
    {
      name: 'Sharing Annotations',
      key: 'sharingAnnotations',
      enabled: !!companyMeetingSettings.sharedAnnotationsEnabled,
    },
    {
      name: 'Messaging',
      key: 'messaging',
      enabled: companyMeetingSettings.CSMessagingEnabled,
    },
    {
      name: 'Discussions',
      key: 'discussions',
      enabled: companyMeetingSettings.CSDiscussionsEnabled,
    },
    {
      name: 'Daily Digest',
      key: 'dailyDigest',
      enabled: !!companyDetails.digestEnabled,
    },
  ];
  return (
    <List
      size='large'
      dataSource={features}
      renderItem={(item) => (
        <List.Item style={{ padding: '16px 0px' }}>
          <Col span={24} style={{ padding: 0 }}>
            <Row align='middle'>
              <Col span={20}>{item.name}</Col>
              <Col span={4} style={{ textAlign: 'right' }}>
                {item.enabled ? (
                  <CheckCircleFilled
                    style={{ color: '#28a745', fontSize: 20 }}
                  />
                ) : (
                  <CloseCircleFilled
                    style={{ color: '#6c757d', fontSize: 20 }}
                  />
                )}
              </Col>
            </Row>
          </Col>
        </List.Item>
      )}
    />
  );
};
